import { useEffect, useRef, useState } from 'react'

import { toast } from 'react-toastify'

import { Icon } from '@loadsmart/miranda-react'

import { answerText } from 'assistant/domain/answerText'
import { statusFromChatError } from 'assistant/domain/chatErrors'
import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'

import FeedbackCommentDialog from './FeedbackCommentDialog'
import { useFeedback } from './hooks/useFeedback'
import { StyledAction, StyledActions } from './MessageActions.styles'

const COPIED_FOR_MS = 2000

type MessageActionsProps = {
  threadId: string
  message: { id: string; parts: readonly { type: string; text?: string }[] }
  /** Only the last answer of an idle thread can be regenerated. */
  canRegenerate: boolean
  onRegenerate: () => void
}

/** Copy, rate and regenerate: what a user does with a finished answer. */
export default function MessageActions({ threadId, message, canRegenerate, onRegenerate }: MessageActionsProps) {
  const { track } = useAnalytics()
  const feedback = useFeedback()
  const [isCopied, setIsCopied] = useState(false)
  const [isAskingComment, setIsAskingComment] = useState(false)
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current)
    },
    []
  )

  const current = feedback.ratingOf(threadId, message.id)

  async function copy() {
    try {
      await navigator.clipboard.writeText(answerText(message))
    } catch {
      toast("Couldn't copy. Select the text and copy it yourself.", { type: 'error' })
      return
    }

    track(AnalyticsEvent.AssistantCopyAnswer, { Thread: threadId })
    setIsCopied(true)

    if (copiedTimer.current) clearTimeout(copiedTimer.current)
    copiedTimer.current = setTimeout(() => setIsCopied(false), COPIED_FOR_MS)
  }

  async function rate(rating: 'up' | 'down') {
    // The API cannot take a rating back, so the current one is not a button.
    if (current?.rating === rating) return

    let isNewest: boolean

    try {
      isNewest = await feedback.save(threadId, message.id, { rating })
    } catch (error) {
      toast(
        statusFromChatError(error) === 404
          ? "Your feedback can't be saved yet. Try again when the answer is complete."
          : "Couldn't save your feedback. Try again.",
        { type: 'error' }
      )
      return
    }

    // The user already chose again: this choice is history, so it asks for nothing.
    if (!isNewest) return

    track(AnalyticsEvent.AssistantFeedback, { Thread: threadId, Rating: rating })

    if (rating === 'down') setIsAskingComment(true)
  }

  return (
    <StyledActions role="group" aria-label="Answer actions">
      <StyledAction type="button" aria-label={isCopied ? 'Copied' : 'Copy answer'} onClick={copy}>
        <Icon name={isCopied ? 'check' : 'copy'} size="16px" />
      </StyledAction>
      <StyledAction
        type="button"
        aria-label="Good answer"
        aria-pressed={current?.rating === 'up'}
        onClick={() => rate('up')}
      >
        <Icon name="like" size="16px" />
      </StyledAction>
      <StyledAction
        type="button"
        aria-label="Bad answer"
        aria-pressed={current?.rating === 'down'}
        onClick={() => rate('down')}
      >
        <Icon name="dislike" size="16px" />
      </StyledAction>
      {canRegenerate && (
        <StyledAction
          type="button"
          aria-label="Regenerate answer"
          onClick={() => {
            track(AnalyticsEvent.AssistantRegenerate, { Thread: threadId })
            onRegenerate()
          }}
        >
          <Icon name="refresh" size="16px" />
          Regenerate
        </StyledAction>
      )}

      {isAskingComment && (
        <FeedbackCommentDialog
          onSave={async comment => {
            await feedback.save(threadId, message.id, { rating: 'down', comment })
          }}
          onClose={() => setIsAskingComment(false)}
        />
      )}
    </StyledActions>
  )
}
